import { toJS } from 'mobx'
import { cartMobx, productMobx, restaurantCenter, RESTAURANT_EVENT, orderMobx } from 'restaurant'
import { buyTogetherMobx, orderLiveMobx, checkoutMobx, multiPersonMobx, multiPersonOrderingMobx } from '@mobxStore'
import barrageController from 'controllers/barrageController'

/**
 * 当前购物车的数据清空和购物车事件注入下单，接单导致的变化分开处理，既事件导致的购物车的变化分开处理各种的后续
 * 关于订单是否接单的状态变化，谁下单谁监听，发生变化后反向给sync状态事件
 */

interface ISyncActionEvent {
  action: 'set' | 'del' | 'clear' | 'emit';
  type?: 'remark';
  data?: any;
}

function convertCartItemToSyncCartItem (cartItem: ICartItem) {
  return {
    type: cartItem.type,
    productNo: cartItem.productNo,
    fkey: cartItem.fkey,
    pk: cartItem.pk,
    scaleNo: cartItem.scaleNo,
    cnt: cartItem.cnt,
    modifierGroups: toJS(cartItem.modifierGroups),
    remark: cartItem.remark,
    addUserId: cartItem.addUserId,
    addUserName: cartItem.addUserName,
    addUserAvatar: cartItem.addUserAvatar,
    isCanTogo: cartItem.isCanTogo,
    isTaxFree: cartItem.isTaxFree,
    isTogo: cartItem.isTogo,
    salesCnt: cartItem.salesCnt,
    isSingleSale: cartItem.isSingleSale,
    isOrderSwapBuy: cartItem.isOrderSwapBuy,
    swapBuyProducts: cartItem.swapBuyProducts,
    productGroups: cartItem.productGroups,
    fullItemNFreeProducts: cartItem.fullItemNFreeProducts
  }
}

class CartLiveMobx {
  /** 事件处理map */
  handlerMap: {
    [key: string]: number;
  } = {}

  init (data: any) {
    // data为初始化服务时云端购物车的数据，需要还原回本地的购物车中
    let cacheAsyncCartItems: any
    if (data?.cartItem) {
      const cartItems = Object.values(data.cartItem)
      // 处理餐品时序，确保进入到购物车时菜单已经准备好了
      if (productMobx.products.length) {
        cartMobx.syncAllToCart(cartItems as any)
      } else {
        restaurantCenter.once(RESTAURANT_EVENT.MENU_END, () => {
          cartMobx.syncAllToCart((cacheAsyncCartItems ? Object.values(cacheAsyncCartItems) : cartItems) as any)
        })
      }
    }
    if (data?.remark) {
      cartMobx.setRemark(data.remark.value, true)
    }

    // 监听修改购物车同步到多人点餐同步服务
    this.handlerMap.CART_ITEMS_CHANGED && restaurantCenter.off(this.handlerMap.CART_ITEMS_CHANGED)
    this.handlerMap.CART_ITEMS_CHANGED = restaurantCenter.on(RESTAURANT_EVENT.CART_ITEMS_CHANGED, ({ type, cartItem, fromSync, syncCnt, notCalcCart }: {
      type: 'remove' | 'change' | 'add';
      cartItem: ICartItem;
      fromSync?: boolean;
      syncCnt?: number;
      notCalcCart?: boolean;
    }) => {
      if (notCalcCart) {
        return
      }
      // 人数变化可能同时调整多个必选餐品；先收集最终值，结束后一次批量提交。
      if (cartMobx.isBatchingRequiredPersonChange) {
        cartMobx.recordRequiredPersonChangeCartItem(cartItem)
        return
      }
      // 如果标记是一个同步触发的 则不进入同步的流程
      if (fromSync) {
        if (type === 'add') {
          const guest = multiPersonMobx.multiPersonMap[cartItem.addUserId]
          if (guest) {
            const theCartItem = cartMobx.fkeyToCartItemMap[cartItem.fkey]
            if (theCartItem && theCartItem.cnt > 0 && guest.nickName) {
              const product = productMobx.noToViewProductMap[theCartItem.productNo]
              if (product) {
                const productName = product.name
                barrageController.show(i18n.t('cart.syncCartBarrage', { user: guest.nickName, product: productName }), guest.avatar, syncCnt)
              }
            }
          }
        }
        return
      } else {
        if (type === 'change' || type === 'add') {
          multiPersonOrderingMobx.handleCartItem('set', convertCartItemToSyncCartItem(cartItem))
        } else if (type === 'remove') {
          // TODO vs set cnt 0 和 del的action
          multiPersonOrderingMobx.handleCartItem('set', convertCartItemToSyncCartItem(cartItem))
        } else if (type === 'clear') {
          // alert('clearMultiCart')
          // multiPersonOrderingMobx.delMultiRoom()
          multiPersonOrderingMobx.clearMultiCart()
        }
      }
    })

    // 监听修改购物车同步到多人点餐同步服务（批量）
    this.handlerMap.CART_ITEMS_BATCH_CHANGED && restaurantCenter.off(this.handlerMap.CART_ITEMS_BATCH_CHANGED)
    this.handlerMap.CART_ITEMS_BATCH_CHANGED = restaurantCenter.on(RESTAURANT_EVENT.CART_ITEMS_BATCH_CHANGED, ({ type, cartItems }: {
      type: 'change';
      cartItems: ICartItem[];
      fromSync?: boolean;
    }) => {
      if (type === 'change') {
        multiPersonOrderingMobx.handleBatchCartItem('set', cartItems.map(cartItem => convertCartItemToSyncCartItem(cartItem)))
      }
    })

    // 监听购物车信息变化同步到多人点餐同步服务
    this.handlerMap.CART_INFO_CHANGED && restaurantCenter.off(this.handlerMap.CART_INFO_CHANGED)
    this.handlerMap.CART_INFO_CHANGED = restaurantCenter.on(RESTAURANT_EVENT.CART_INFO_CHANGED, ({ remark, fromSync }: {
      remark?: string;
      fromSync?: boolean;
    }) => {
      // 如果标记是一个同步触发的 则不进入同步的流程
      if (fromSync) {
        return
      }
      const theAddUserId = multiPersonMobx.currentPersonInfo?.personId
      multiPersonOrderingMobx.handleRemark({
        addUser: theAddUserId,
        value: remark || ''
      })
    })

    // 监听下单触发到多人点餐同步服务
    this.handlerMap.SAVE_ORDER && restaurantCenter.off(this.handlerMap.SAVE_ORDER)
    this.handlerMap.SAVE_ORDER = restaurantCenter.on(RESTAURANT_EVENT.SAVE_ORDER, ({ fromSync, order }) => {
      if (fromSync) {
        orderLiveMobx.setIsMultiEditing(false)
      } else {
        // 下单会分成2种情况需要接单和不需要接单
        const theAddUserId = multiPersonMobx.currentPersonInfo?.personId
        multiPersonOrderingMobx.emitOrder({
          action: 'saveOrder',
          status: order.status,
          ordersId: order.ordersId,
          addUser: theAddUserId
        })
        const currentPerson = multiPersonMobx.getCurrentPersonInfo()
        if (Config.buyTogetherId) {
          if (currentPerson.isOwner) {
            multiPersonMobx.setCurrentPersonInfo({
              ...currentPerson,
              ordersId: order.ordersId
            })
          }
          const buyTogetherStorage = buyTogetherMobx.getStorage()
          if (buyTogetherStorage) {
            buyTogetherMobx.setStorage({
              ...buyTogetherStorage,
              ordersId: order.ordersId
            })
          }
        }
      }
    })

    // 监听取消订单触发同步服务
    this.handlerMap.CANCEL_ORDER && restaurantCenter.off(this.handlerMap.CANCEL_ORDER)
    this.handlerMap.CANCEL_ORDER = restaurantCenter.on(RESTAURANT_EVENT.CANCEL_ORDER, ({ fromSync, order }) => {
      if (fromSync) {
      } else {
        const theAddUserId = multiPersonMobx.currentPersonInfo?.personId
        multiPersonOrderingMobx.emitOrder({
          action: 'cancelOrder',
          ordersId: order.ordersId,
          addUser: theAddUserId
        })
      }
    })

    // 当多人修改触发云端购物车删除时 1. 修改订单状态 2. 如果在结算页则模版
    this.handlerMap.ORDER_CART_DELETED && restaurantCenter.off(this.handlerMap.ORDER_CART_DELETED)
    this.handlerMap.ORDER_CART_DELETED = restaurantCenter.on(RESTAURANT_EVENT.ORDER_CART_DELETED, ({ fromSync, data: IOrder }) => {
      if (fromSync) {
        if (data.status === 'Pending') {
          orderMobx.setStatus('Pending')
          orderLiveMobx.setIsMultiEditing(true)
        }
      } else {
        const theAddUserId = multiPersonMobx.currentPersonInfo?.personId
        multiPersonOrderingMobx.emitOrder({
          action: 'delCart',
          ordersId: data.ordersId,
          addUser: theAddUserId
        })
      }
    })

    // 用餐人数本地缓存；同步发送在建立房间连接时已注册。
    this.handlerMap.EATING_PERSON_CHANGE && restaurantCenter.off(this.handlerMap.EATING_PERSON_CHANGE)
    this.handlerMap.EATING_PERSON_CHANGE = restaurantCenter.on(RESTAURANT_EVENT.EATING_PERSON_CHANGE, ({ data }: { data: { cnt: number; kids?: number } }) => {
      multiPersonMobx.personCntInfo = data
    })

    // 监听同步服务购物车变化
    multiPersonOrderingMobx.onCartItemChange((operation, cartItems?: TCartItem) => {
      cartItems && (cacheAsyncCartItems = cartItems as any)
      if (operation.action === 'set' && operation.type === 'cartItem') {
        // 进入购物车中才能获取到转换后的购物车view数据
        cartMobx.syncToCart(operation.cartItem as ISyncCartItem)
      } else if (operation.action === 'clear' && operation.type === 'cartItem') {
        cartMobx.syncClear()
      } else if (operation.action === 'switch' && operation.type === 'cartItem') { // 购物车全量更新
        cartItems && cartMobx.syncAllToCart(Object.values(cartItems) as any)
      }
    })

    // 监听同步服务备注变化
    multiPersonOrderingMobx.onRemarkChange((remark: string) => {
      cartMobx.setRemark(remark || '', true)
      checkoutMobx.resetRemarkInfo()
    })

    // 监听点餐人数变化
    multiPersonOrderingMobx.onPersonNumberChange((person: { cnt: number; kids?: number }) => {
      // 远端人数只同步状态，不在每个客户端重复调整必选餐品数量。
      multiPersonMobx.setOrderPerson(person, true)
    })

    // 监听同步服务订单触发事件
    multiPersonOrderingMobx.onOrderEmit((data) => {
      if (data.action === 'saveOrder') {
        restaurantCenter.emit(RESTAURANT_EVENT.SAVE_ORDER, {
          fromSync: true,
          order: {
            ordersId: data.ordersId,
            addUser: data.addUser
          }
        })
      } else if (data.action === 'cancelOrder') {
        restaurantCenter.emit(RESTAURANT_EVENT.CANCEL_ORDER, {
          fromSync: true,
          order: {
            ordersId: data.ordersId,
            addUser: data.addUser
          }
        })
      } else if (data.action === 'delCart') {
        restaurantCenter.emit(RESTAURANT_EVENT.ORDER_CART_DELETED, {
          fromSync: true,
          order: {
            status: 'Pending',
            ordersId: data.ordersId
          }
        })
      } else if (data.action === 'orders.statusChanged') {
        restaurantCenter.emit(RESTAURANT_EVENT.ORDER_STATUS_CHANGED, {
          fromSync: true,
          order: {
            ordersId: data.ordersId,
            status: data.status,
            mqAction: data.mqAction,
          }
        })
      } else if (data.action === 'orders.tableStatusChange') {
        // 拒绝接单
        if (data.tableCodeClean && data.tableAliasClean && data.tableCodeOccupy && data.tableAliasOccupy) {
          restaurantCenter.emit(RESTAURANT_EVENT.ORDER_TABLE_CHANGED, {
            fromSync: true,
            order: {
              tableCodeClean: data.tableCodeClean,
              tableAliasClean: data.tableAliasClean,
              tableCodeOccupy: data.tableCodeOccupy,
              tableAliasOccupy: data.tableAliasOccupy
            }
          })
        } else {
          restaurantCenter.emit(RESTAURANT_EVENT.CANCEL_ORDER, {
            fromSync: true,
            order: {
              ordersId: data.ordersId,
            }
          })
        }
      } else if (data.action === 'quitBB') { // 拼单发起者退出拼单
        multiPersonOrderingMobx.delMultiRoom()
        multiPersonMobx.clearMultiPersonMap()
      }
    })
  }
}
export default new CartLiveMobx()
